// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { SearchFoundationsInput } from '@lfx/core/gql';
import { Injectable } from '@angular/core';
import {
  Foundation,
  FoundationDomain,
  FoundationProject,
  FoundationFinance,
  Resource,
  Statistics,
  UserGroup,
  UserGroupType,
  DomainAvailability,
  DomainInfo,
  DomainInfoResult,
  FoundationMeeting,
  ActivityLog,
  ProjectType,
} from '@models';
import { Observable, Subject } from 'rxjs';
import {
  FoundationServiceGql,
  CreateNewFoundationPayload,
  UpdateFoundationLegalInput,
  UpdateFoundationLegalPayload,
  AdminServiceGql,
  ProjectServiceGql,
} from '../gql';
import { map, take } from 'rxjs/operators';
import { isEmpty } from 'lodash';
import { generalConstants } from '@lfx/config';

@Injectable({
  providedIn: 'root',
})
export class FoundationService {
  foundationAdminForm: Subject<string> = new Subject<string>();

  constructor(
    private foundationServiceGql: FoundationServiceGql,
    private adminServiceGql: AdminServiceGql,
    private projectServiceGql: ProjectServiceGql
  ) {}

  public getMyFoundations(
    withMembersData = true,
    withProjectsData = true,
    withProgramManagerData = false
  ): Observable<Foundation[]> {
    return this.foundationServiceGql.getMyFoundations(
      withMembersData,
      withProjectsData,
      withProgramManagerData
    );
  }

  public getSuggestedFoundations(): Observable<Foundation[]> {
    return this.foundationServiceGql.getSuggestedFoundations();
  }

  public getNonMemberFoundations(
    withMembersData = true,
    withProjectsData = true
  ): Observable<Foundation[]> {
    return this.foundationServiceGql.getNonMemberFoundations(
      withMembersData,
      withProjectsData
    );
  }

  public getLfSponsoredProjects(): Observable<FoundationProject[]> {
    return this.projectServiceGql.getLfSponsoredProjects();
  }

  public getFoundations(
    withMembersData = false,
    noCache?: boolean
  ): Observable<Foundation[]> {
    return this.foundationServiceGql.getAllFoundations(
      withMembersData,
      noCache
    );
  }

  public claEnabledFoundations(): Observable<Foundation[]> {
    return this.foundationServiceGql.claEnabledFoundations();
  }

  public searchFoundation(query?: SearchFoundationsInput, noCache?: boolean) {
    return this.foundationServiceGql.searchFoundations(query, noCache);
  }

  public getFoundationDomains(foundationId): Observable<FoundationDomain[]> {
    return this.foundationServiceGql.getFoundationDomains(foundationId);
  }

  public checkDomainAvailability(
    domain: string
  ): Observable<DomainAvailability> {
    return this.adminServiceGql.checkDomainAvailability(domain);
  }

  public registerDomain(domain: DomainInfo): Observable<DomainInfoResult> {
    return this.adminServiceGql.addNewDomain(domain);
  }

  public uploadContractFile(file) {
    return this.foundationServiceGql.uploadContractDoc(file);
  }

  public removeUploadedContractFile(key) {
    return this.foundationServiceGql.removeUploadedContractDoc(key);
  }

  public getFoundation(
    id: string,
    withAdminData = false,
    withProjectsData = false,
    withMembersData = false,
    withContributionsData = false,
    noCache = false,
    communityYearEvent = 'all',
    organizationYearEvent = 'all'
  ): Observable<Foundation> {
    return this.foundationServiceGql.getFoundation(
      id,
      withAdminData,
      withProjectsData,
      withMembersData,
      withContributionsData,
      noCache,
      communityYearEvent,
      organizationYearEvent
    );
  }

  public getFoundationForStatistics(
    id: string,
    filter?: string
  ): Observable<Foundation> {
    return this.foundationServiceGql.getFoundationForStatistics(id, filter);
  }

  public getFoundationById(
    id: string,
    withAdminData = false,
    withProjectsData = false,
    withMembersData = false,
    noCache = false
  ): Observable<Foundation> {
    return this.foundationServiceGql.getFoundation(
      id,
      withAdminData,
      withProjectsData,
      withMembersData,
      noCache
    );
  }

  public updateFoundationEssential(input: any): Observable<Foundation> {
    return this.foundationServiceGql.updateFoundationEssential(input.data);
  }

  public getAllProjects(noCache = false): Observable<FoundationProject[]> {
    return this.projectServiceGql.getAllProjects(noCache);
  }

  public getFoundationStatistics(id: string): Observable<Statistics> {
    return this.foundationServiceGql.getFoundationStatistics(id);
  }

  public getFoundationCommittees(id: string): Observable<UserGroup[]> {
    return this.foundationServiceGql.getFoundationCommittees(id);
  }

  public getFoundationResources(id: string): Observable<Resource[]> {
    return this.foundationServiceGql.getFoundationResources(id);
  }

  public getFoundationMeetings(id: string): Observable<FoundationMeeting> {
    return this.foundationServiceGql.getFoundationMeetings(id);
  }

  public updateFoundationLegal(
    input: UpdateFoundationLegalInput
  ): Observable<UpdateFoundationLegalPayload> {
    return this.foundationServiceGql.updateFoundationLegal(input);
  }

  public updateFoundationFinance(
    foundationId: string,
    foundationFinanceInfo: FoundationFinance
  ): Observable<FoundationFinance> {
    if (foundationFinanceInfo) {
      return this.foundationServiceGql.updateFoundationFinance({
        id: foundationId,
        ...foundationFinanceInfo,
      });
    } else {
      return null;
    }
  }

  public createNewFoundation(
    foundationName: string
  ): Observable<CreateNewFoundationPayload> {
    return this.foundationServiceGql.creatNewFoundation({ foundationName });
  }

  public createFoundationCommittees(
    foundationId: string,
    committees: UserGroup[]
  ): Observable<UserGroup[]> {
    const committeesInput = { foundationId, committees: [] };

    committees.forEach(committee => {
      const committeeInput = {
        category: Object.keys(UserGroupType).find(
          x => UserGroupType[x] === committee.category
        ),
        collaborationName: committee.collaborationName,
        description: committee.description,
        url: committee.url,
      };

      committeesInput.committees.push(committeeInput);
    });

    return this.foundationServiceGql.createFoundationCommittees(
      committeesInput
    );
  }

  public updateCommittee(
    foundationId: string,
    committee: UserGroup
  ): Observable<UserGroup> {
    const updateCommitteeObj = {
      category: committee.category,
      collaborationName: committee.collaborationName,
      description: committee.description,
      url: committee.url,
    };
    const updateCommitteeInput = {
      foundationId,
      committeeId: committee.id,
      committee: updateCommitteeObj,
    };

    return this.foundationServiceGql.updateFoundationCommittee(
      updateCommitteeInput
    );
  }

  public removeCommittee(
    foundationId: string,
    committeeId: string
  ): Observable<boolean> {
    const deleteCommitteeInput = {
      foundationId,
      committeeId,
    };

    return this.foundationServiceGql.deleteFoundationCommittee(
      deleteCommitteeInput
    );
  }

  getFoundationMemberShips(foundationId: string): Observable<Foundation> {
    return this.foundationServiceGql.getFoundationMemberShips(foundationId);
  }

  getFoundationActivityLogs(input): Observable<ActivityLog[]> {
    return this.foundationServiceGql.getFoundationActivityLogs(input).pipe(
      map(res => {
        if (res === null) {
          return [];
        }

        return res;
      })
    );
  }

  getProjectType(project: Foundation): ProjectType {
    if (project.projectType === ProjectType.projectGroup && !project.parent) {
      return ProjectType.projectGroup;
    } else if (this.isStandAloneProject(project)) {
      return ProjectType.standAlone;
    }

    return ProjectType.project;
  }

  isStandAloneProject(project: Foundation) {
    return (
      ((!project.parent || isEmpty(project.parent)) &&
        project.projectType === ProjectType.project) ||
      (project.foundation &&
        project.foundation.name === generalConstants.standAloneFoundationName)
    );
  }

  getStandAloneProjectsFoundationId(): Observable<string> {
    return this.searchFoundation({
      name: [generalConstants.standAloneFoundationName],
    }).pipe(
      take(1),
      map(foundations => {
        if (foundations && !isEmpty(foundations)) {
          return foundations[0].id;
        }
      })
    );
  }

  getTheLinuxFoundationData(): Observable<Foundation[]> {
    return this.searchFoundation({
      name: [generalConstants.linuxFoundationProjectName],
    });
  }
}
